import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef, useState } from "react";
import {
    ActivityIndicator,
    Animated,
    Easing,
    Platform,
    Pressable,
    StyleSheet,
    View
} from "react-native";
import { Text } from "react-native-paper";

import { Chapter } from "./Chapter";
import { FullBleedBand } from "./FullBleedBand";

import { colors, fonts, gradients } from "@/lib/theme";

/**
 * Validation phase — drives the band tone + content. Same names the
 * orchestrator's completePhase uses.
 *
 *   idle         RN hasn't tapped Complete yet (or section is reset).
 *   validating   /ai/validate is running for the first time.
 *   errors       Validation came back with errors (initial view, before
 *                "Take me to first item").
 *   resolve      User has been auto-scrolled to first flagged question; band
 *                stays amber at the bottom as persistent context.
 *   resolved     User dismissed/handled all errors. Awaiting Re-validate.
 *   revalidating /ai/validate running again after fixes.
 *   clear        Validation came back with zero errors. Auto-seal in 1.2s.
 *   failed       Network/LLM error. Retry / Close.
 */
export type CompletePhase =
    | "idle"
    | "validating"
    | "errors"
    | "resolve"
    | "resolved"
    | "revalidating"
    | "clear"
    | "failed";

const USE_NATIVE_DRIVER = Platform.OS !== "web";

interface Props {
    phase: CompletePhase;
    /** Active validation error count. Drives the "N items to resolve" line. */
    remainingCount: number;
    /** Count of severity === "hard" errors among the remaining. */
    requiredCount: number;
    /** Count of severity === "suggested" errors among the remaining. */
    suggestedCount: number;
    readOnly: boolean;
    /** Stage of the validating band (0..3) — bumped externally. */
    validatingStageIdx: number;

    onValidate: () => void;
    onTakeMeToFirst: () => void;
    onRevalidate: () => void;
    onRetry: () => void;
    onCloseFailed: () => void;
}

/**
 * Chapter 04 — band-driven validation lifecycle.
 *
 * Phase machine port of validation-errors-flow/SPEC.md. The band wraps the
 * entire chapter; its tone (teal/amber/ok/err) communicates phase. The
 * Complete pill is only visible in `idle`; every other phase replaces it
 * with a band.
 *
 * Visit 127 discipline: NO silent-advance on `failed`. The user explicitly
 * Retries or Closes.
 */
export function CompleteChapter({
    phase,
    remainingCount,
    requiredCount,
    suggestedCount,
    readOnly,
    validatingStageIdx,
    onValidate,
    onTakeMeToFirst,
    onRevalidate,
    onRetry,
    onCloseFailed
}: Props) {
    if (phase === "idle") {
        return (
            <Chapter num="04" title="Complete" helper="AI clinical review.">
                <IdlePill onPress={onValidate} disabled={readOnly} />
            </Chapter>
        );
    }
    if (phase === "validating" || phase === "revalidating") {
        return (
            <Chapter num="04" title="Complete" helper="AI clinical review.">
                <FullBleedBand tone="teal" scanFast>
                    <ValidatingBandContent stageIdx={validatingStageIdx} />
                </FullBleedBand>
            </Chapter>
        );
    }
    if (phase === "errors" || phase === "resolve") {
        return (
            <Chapter num="04" title="Complete" helper="AI clinical review.">
                <FullBleedBand tone="amber">
                    <ErrorsBandContent
                        remaining={remainingCount}
                        required={requiredCount}
                        suggested={suggestedCount}
                        onJump={onTakeMeToFirst}
                    />
                </FullBleedBand>
            </Chapter>
        );
    }
    if (phase === "resolved") {
        return (
            <Chapter num="04" title="Complete" helper="AI clinical review.">
                <FullBleedBand tone="ok">
                    <ResolvedBandContent onRevalidate={onRevalidate} disabled={readOnly} />
                </FullBleedBand>
            </Chapter>
        );
    }
    if (phase === "clear") {
        return (
            <Chapter num="04" title="Complete" helper="AI clinical review.">
                <FullBleedBand tone="ok">
                    <AllClearBandContent />
                </FullBleedBand>
            </Chapter>
        );
    }
    return (
        <Chapter num="04" title="Complete" helper="AI clinical review.">
            <FullBleedBand tone="err">
                <FailedBandContent onRetry={onRetry} onClose={onCloseFailed} />
            </FullBleedBand>
        </Chapter>
    );
}

// ── Phase: idle ────────────────────────────────────────────────────────────

function IdlePill({ onPress, disabled }: { onPress: () => void; disabled: boolean }) {
    return (
        <Pressable
            onPress={onPress}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel="Complete section"
            style={({ pressed }) => [pressed && !disabled && { opacity: 0.85 }]}
        >
            <LinearGradient
                colors={[...gradients.brand]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={[styles.idleCta, disabled && { opacity: 0.5 }]}
            >
                <Text style={styles.idleCtaText}>Complete section</Text>
                <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
            </LinearGradient>
        </Pressable>
    );
}

// ── Phase: validating / revalidating ──────────────────────────────────────

const STAGES = [
    "Reading answers",
    "Cross-referencing diagnoses",
    "Cross-referencing medications",
    "Checking patient condition"
] as const;

function ValidatingBandContent({ stageIdx }: { stageIdx: number }) {
    return (
        <View style={{ gap: 14 }}>
            <View style={cstyles.header}>
                <View style={cstyles.orb}>
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={cstyles.orbFill}
                    >
                        <MaterialCommunityIcons name="creation" size={15} color="#fff" />
                    </LinearGradient>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={cstyles.smallcapsPink}>Reviewing</Text>
                    <Text style={cstyles.h1Ink}>Running clinical review</Text>
                </View>
            </View>
            <View style={{ gap: 6 }}>
                {STAGES.map((label, i) => {
                    const isDone = i < stageIdx;
                    const isActive = i === stageIdx;
                    return (
                        <StageRow
                            key={label}
                            index={i}
                            label={label}
                            isDone={isDone}
                            isActive={isActive}
                        />
                    );
                })}
            </View>
        </View>
    );
}

function StageRow({
    index,
    label,
    isDone,
    isActive
}: {
    index: number;
    label: string;
    isDone: boolean;
    isActive: boolean;
}) {
    const fadeUp = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        Animated.timing(fadeUp, {
            toValue: 1,
            duration: 280,
            delay: index * 70,
            easing: Easing.out(Easing.ease),
            useNativeDriver: USE_NATIVE_DRIVER
        }).start();
    }, [fadeUp, index]);

    return (
        <Animated.View
            style={[
                cstyles.stageRow,
                {
                    backgroundColor: isActive ? "rgba(255,255,255,0.78)" : "rgba(255,255,255,0.55)",
                    borderColor: isActive
                        ? "rgba(69,210,176,0.55)"
                        : isDone
                          ? "rgba(69,210,176,0.25)"
                          : "rgba(11,11,31,0.06)",
                    opacity: fadeUp,
                    transform: [
                        {
                            translateY: fadeUp.interpolate({
                                inputRange: [0, 1],
                                outputRange: [10, 0]
                            })
                        }
                    ]
                }
            ]}
        >
            <View
                style={[
                    cstyles.stageBullet,
                    isDone
                        ? { backgroundColor: colors.teal600 }
                        : isActive
                          ? { backgroundColor: colors.teal400 }
                          : { backgroundColor: "rgba(11,11,31,0.06)" }
                ]}
            >
                {isDone ? (
                    <MaterialCommunityIcons name="check" size={9} color="#fff" />
                ) : isActive ? (
                    <ActivityIndicator size="small" color="#fff" />
                ) : null}
            </View>
            <Text
                style={[
                    cstyles.stageLabel,
                    (isActive || isDone) && { color: colors.ink, fontFamily: fonts.extraBold }
                ]}
            >
                {label}
            </Text>
        </Animated.View>
    );
}

// ── Phase: errors / resolve ───────────────────────────────────────────────

function ErrorsBandContent({
    remaining,
    required,
    suggested,
    onJump
}: {
    remaining: number;
    required: number;
    suggested: number;
    onJump: () => void;
}) {
    return (
        <View style={{ gap: 12 }}>
            <View style={cstyles.header}>
                <View style={[cstyles.statusCircle, { backgroundColor: colors.warning500 }]}>
                    <MaterialCommunityIcons name="alert" size={16} color="#fff" />
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={cstyles.smallcapsAmber}>Review needed</Text>
                    <Text style={cstyles.h2Ink}>{remaining} items to resolve</Text>
                </View>
            </View>
            <Text style={cstyles.body}>
                {required} required · {suggested} suggested · scroll up to address each one.
            </Text>
            <Pressable
                onPress={onJump}
                accessibilityRole="button"
                accessibilityLabel="Take me to the first item"
                style={({ pressed }) => [cstyles.darkCta, pressed && { opacity: 0.85 }]}
            >
                <Text style={cstyles.darkCtaText}>Take me to the first item</Text>
                <MaterialCommunityIcons name="arrow-right" size={15} color="#fff" />
            </Pressable>
        </View>
    );
}

// ── Phase: resolved ──────────────────────────────────────────────────────

function ResolvedBandContent({
    onRevalidate,
    disabled
}: {
    onRevalidate: () => void;
    disabled: boolean;
}) {
    return (
        <View style={{ gap: 12 }}>
            <View style={cstyles.header}>
                <View style={[cstyles.statusCircle, { backgroundColor: colors.success500 }]}>
                    <MaterialCommunityIcons name="check" size={15} color="#fff" />
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={cstyles.smallcapsGreen}>Ready</Text>
                    <Text style={cstyles.h2Ink}>All items resolved.</Text>
                </View>
            </View>
            <Text style={cstyles.body}>Re-run the review to confirm everything checks out.</Text>
            <Pressable
                onPress={onRevalidate}
                disabled={disabled}
                accessibilityRole="button"
                accessibilityLabel="Re-validate"
                style={({ pressed }) => [pressed && !disabled && { opacity: 0.85 }]}
            >
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={cstyles.brandCta}
                >
                    <MaterialCommunityIcons name="creation" size={15} color="#fff" />
                    <Text style={cstyles.brandCtaText}>Re-validate</Text>
                </LinearGradient>
            </Pressable>
        </View>
    );
}

// ── Phase: clear ──────────────────────────────────────────────────────────

function AllClearBandContent() {
    const pop = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        Animated.sequence([
            Animated.timing(pop, {
                toValue: 1.08,
                duration: 260,
                easing: Easing.out(Easing.ease),
                useNativeDriver: USE_NATIVE_DRIVER
            }),
            Animated.timing(pop, {
                toValue: 1,
                duration: 140,
                easing: Easing.inOut(Easing.ease),
                useNativeDriver: USE_NATIVE_DRIVER
            })
        ]).start();
    }, [pop]);

    return (
        <View style={cstyles.clearCol}>
            <Animated.View
                style={[
                    cstyles.successCircle,
                    {
                        transform: [{ scale: pop }],
                        opacity: pop.interpolate({
                            inputRange: [0, 1],
                            outputRange: [0, 1]
                        })
                    }
                ]}
            >
                <MaterialCommunityIcons name="check" size={26} color="#fff" />
            </Animated.View>
            <Text style={cstyles.h1Ink}>All clear.</Text>
            <Text style={cstyles.body}>Section sealed · returning…</Text>
        </View>
    );
}

// ── Phase: failed ─────────────────────────────────────────────────────────

function FailedBandContent({ onRetry, onClose }: { onRetry: () => void; onClose: () => void }) {
    return (
        <View style={{ gap: 12 }}>
            <View style={cstyles.header}>
                <View style={[cstyles.statusCircle, { backgroundColor: colors.error500 }]}>
                    <MaterialCommunityIcons name="close" size={16} color="#fff" />
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={cstyles.smallcapsErr}>Review unavailable</Text>
                    <Text style={cstyles.h2Ink}>Clinical review failed.</Text>
                </View>
            </View>
            <Text style={cstyles.body}>
                Try again or close to go back. We will NOT auto-advance.
            </Text>
            <View style={cstyles.failedRow}>
                <Pressable
                    onPress={onClose}
                    accessibilityRole="button"
                    accessibilityLabel="Close"
                    style={({ pressed }) => [cstyles.ghostCta, pressed && { opacity: 0.85 }]}
                >
                    <Text style={cstyles.ghostCtaText}>Close</Text>
                </Pressable>
                <Pressable
                    onPress={onRetry}
                    accessibilityRole="button"
                    accessibilityLabel="Retry"
                    style={({ pressed }) => [{ flex: 1 }, pressed && { opacity: 0.85 }]}
                >
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={cstyles.brandCta}
                    >
                        <Text style={cstyles.brandCtaText}>Retry</Text>
                    </LinearGradient>
                </Pressable>
            </View>
        </View>
    );
}

// ── Animation hook for the orchestrator ───────────────────────────────────

/**
 * Drives the stageIdx 0..3 over ~9.6s. Reset whenever phase enters
 * "validating" or "revalidating". Returns the current stageIdx.
 */
export function useValidatingStageIdx(active: boolean): number {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        if (!active) {
            setIdx(0);
            return;
        }
        setIdx(0);
        const ticks = [2400, 4800, 7200, 9600];
        const timeouts = ticks.map((ms, i) => setTimeout(() => setIdx(i + 1), ms));
        return () => {
            timeouts.forEach(clearTimeout);
        };
    }, [active]);
    return idx;
}

// ── Styles ────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
    idleCta: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        paddingVertical: 20,
        paddingHorizontal: 22,
        borderRadius: 999,
        ...Platform.select({
            ios: {
                shadowColor: "#45D2B0",
                shadowOffset: { width: 0, height: 18 },
                shadowOpacity: 0.4,
                shadowRadius: 32
            },
            android: { elevation: 8 },
            default: {}
        })
    },
    idleCtaText: {
        fontFamily: fonts.extraBold,
        fontSize: 16,
        color: "#fff",
        letterSpacing: -0.2
    }
});

const cstyles = StyleSheet.create({
    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10
    },
    orb: {
        width: 30,
        height: 30,
        borderRadius: 15,
        overflow: "hidden",
        ...Platform.select({
            ios: {
                shadowColor: "#45D2B0",
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.3,
                shadowRadius: 12
            },
            android: { elevation: 6 },
            default: {}
        })
    },
    orbFill: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center"
    },
    statusCircle: {
        width: 30,
        height: 30,
        borderRadius: 15,
        alignItems: "center",
        justifyContent: "center",
        ...Platform.select({
            ios: {
                shadowColor: "#0B0B1F",
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.18,
                shadowRadius: 12
            },
            android: { elevation: 6 },
            default: {}
        })
    },
    smallcapsPink: {
        fontFamily: fonts.extraBold,
        fontSize: 10.5,
        color: colors.pink600,
        letterSpacing: 1,
        textTransform: "uppercase"
    },
    smallcapsAmber: {
        fontFamily: fonts.extraBold,
        fontSize: 10.5,
        color: colors.warning600,
        letterSpacing: 1,
        textTransform: "uppercase"
    },
    smallcapsGreen: {
        fontFamily: fonts.extraBold,
        fontSize: 10.5,
        color: "#047857",
        letterSpacing: 1,
        textTransform: "uppercase"
    },
    smallcapsErr: {
        fontFamily: fonts.extraBold,
        fontSize: 10.5,
        color: colors.error600,
        letterSpacing: 1,
        textTransform: "uppercase"
    },
    h1Ink: {
        fontFamily: fonts.extraBold,
        fontSize: 22,
        color: colors.ink,
        letterSpacing: -0.4
    },
    h2Ink: {
        fontFamily: fonts.extraBold,
        fontSize: 18,
        color: colors.ink,
        letterSpacing: -0.3
    },
    body: {
        fontFamily: fonts.medium,
        fontSize: 12.5,
        color: colors.textSecondary,
        lineHeight: 19
    },
    stageRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderWidth: 1,
        borderRadius: 8
    },
    stageBullet: {
        width: 16,
        height: 16,
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center"
    },
    stageLabel: {
        flex: 1,
        fontFamily: fonts.bold,
        fontSize: 11.5,
        color: colors.textSecondary
    },
    darkCta: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 13,
        paddingHorizontal: 16,
        borderRadius: 999,
        backgroundColor: colors.ink,
        ...Platform.select({
            ios: {
                shadowColor: "#0B0B1F",
                shadowOffset: { width: 0, height: 14 },
                shadowOpacity: 0.25,
                shadowRadius: 28
            },
            android: { elevation: 6 },
            default: {}
        })
    },
    darkCtaText: {
        fontFamily: fonts.extraBold,
        fontSize: 13.5,
        color: "#fff",
        letterSpacing: -0.2
    },
    brandCta: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 14,
        paddingHorizontal: 16,
        borderRadius: 999,
        ...Platform.select({
            ios: {
                shadowColor: "#45D2B0",
                shadowOffset: { width: 0, height: 16 },
                shadowOpacity: 0.4,
                shadowRadius: 32
            },
            android: { elevation: 8 },
            default: {}
        })
    },
    brandCtaText: {
        fontFamily: fonts.extraBold,
        fontSize: 14,
        color: "#fff",
        letterSpacing: -0.2
    },
    clearCol: {
        alignItems: "center",
        gap: 8,
        paddingVertical: 8
    },
    successCircle: {
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: colors.success500,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 8,
        ...Platform.select({
            ios: {
                shadowColor: colors.success500,
                shadowOffset: { width: 0, height: 14 },
                shadowOpacity: 0.4,
                shadowRadius: 28
            },
            android: { elevation: 8 },
            default: {}
        })
    },
    failedRow: {
        flexDirection: "row",
        gap: 10
    },
    ghostCta: {
        paddingVertical: 14,
        paddingHorizontal: 18,
        borderRadius: 999,
        backgroundColor: "transparent",
        borderWidth: 1.5,
        borderColor: colors.gray300,
        alignItems: "center",
        justifyContent: "center"
    },
    ghostCtaText: {
        fontFamily: fonts.bold,
        fontSize: 13.5,
        color: colors.textSecondary
    }
});
